import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { useI18n } from '../../lib/i18n.js';
import { Card } from '../../components/ui.js';
import { QueryState } from '../../components/form.js';
import { TrendChart, type TrendPoint } from '../../components/charts.js';

interface SeriesPoint { date: string; value: string; unit: string; referenceLow: string | null; referenceHigh: string | null }
interface TrendsResp { series: Record<string, SeriesPoint[]> }
interface Growth { date: string; weightGrams: string | null; headCircumferenceCm: string | null }

const num = (s: string | null | undefined) => (s == null || s === '' ? null : Number(s));

function toPoints(series: SeriesPoint[] | undefined): { points: TrendPoint[]; unit?: string; low: number | null; high: number | null } {
  if (!series?.length) return { points: [], low: null, high: null };
  return {
    points: series.map((p) => ({ date: p.date, value: Number(p.value), referenceLow: num(p.referenceLow), referenceHigh: num(p.referenceHigh) })),
    unit: series[0]?.unit,
    low: num(series[0]?.referenceLow),
    high: num(series[0]?.referenceHigh),
  };
}

export function ParentTrends() {
  const { t } = useI18n();
  const labs = useQuery({ queryKey: ['trends'], queryFn: () => api.get<TrendsResp>('/labs/trends') });
  const growth = useQuery({ queryKey: ['growth'], queryFn: () => api.get<{ growth: Growth[] }>('/growth') });

  const ANALYTES = ['hemoglobin', 'neutrophils_abs', 'platelets'] as const;
  const growthAsc = [...(growth.data?.growth ?? [])].sort((a, b) => (a.date < b.date ? -1 : 1));
  const weightPts: TrendPoint[] = growthAsc.filter((g) => g.weightGrams).map((g) => ({ date: g.date, value: Number(g.weightGrams) }));
  const hcPts: TrendPoint[] = growthAsc.filter((g) => g.headCircumferenceCm).map((g) => ({ date: g.date, value: Number(g.headCircumferenceCm) }));

  return (
    <div>
      <h1 className="text-xl font-bold text-slate-800 mb-4">{t('exam.nfs')}</h1>
      <QueryState isLoading={labs.isLoading} isError={labs.isError} error={labs.error} emptyMessage="—" errorFallback={t('common.error')}>
        {ANALYTES.map((key) => {
          const { points, unit, low, high } = toPoints(labs.data?.series[key]);
          if (!points.length) return null;
          return (
            <Card key={key} title={t(`analyte.${key}`)}>
              <TrendChart data={points} unit={unit} referenceLow={low} referenceHigh={high} />
            </Card>
          );
        })}
      </QueryState>

      <h1 className="text-xl font-bold text-slate-800 mt-6 mb-4">{t('growth.weight')} / {t('growth.headCircumference')}</h1>
      <QueryState isLoading={growth.isLoading} isError={growth.isError} error={growth.error} emptyMessage="—" errorFallback={t('common.error')}>
        {weightPts.length > 0 && (
          <Card title={`${t('growth.weight')} (g)`}>
            <TrendChart data={weightPts} unit="g" color="#0e7490" />
          </Card>
        )}
        {hcPts.length > 0 && (
          <Card title={`${t('growth.headCircumference')} (cm)`}>
            <TrendChart data={hcPts} unit="cm" color="#7c3aed" />
          </Card>
        )}
      </QueryState>
    </div>
  );
}
